import {
    Card,
    CardContent,
    Fade,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
    camelCaseToLowerCase,
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
} from '../../../shared/utils';
import IndividualExperienceSpacesModal from './IndividualExperienceSpacesModal';
import { getAmenitiesArray, getBeddingArray } from './Spaces/IndividualSpace';
import { Hotel } from '@mui/icons-material';
import WeekendIcon from '@mui/icons-material/Weekend';
import GroupsIcon from '@mui/icons-material/Groups';
import ShowerIcon from '@mui/icons-material/Shower';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import KitchenIcon from '@mui/icons-material/Kitchen';
import { SpaceType } from '../../enums/experience';
import { Desk } from '@mui/icons-material';

export const SpaceIcons: Record<any, React.ReactNode> = {
    [SpaceType.Bedroom]: <Hotel />,
    [SpaceType.LivingRoom]: <WeekendIcon />,
    [SpaceType.Boardroom]: <GroupsIcon />,
    [SpaceType.Bathroom]: <ShowerIcon />,
    [SpaceType.DiningArea]: <RestaurantIcon />,
    [SpaceType.GameRoom]: <SportsEsportsIcon />,
    [SpaceType.Garage]: <DirectionsCarIcon />,
    [SpaceType.Kitchen]: <KitchenIcon />,
    [SpaceType.PrivateOffice]: <Desk />,
};

const SpaceCard = ({
    space,
    selected,
    spaces,
    maxNumberOfBeds,
    cursor,
}: {
    space: any;
    spaces?: any;
    selected: boolean;
    maxNumberOfBeds: number;
    cursor: string;
}) => {
    const [showModal, setShowModal] = useState(false);
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();

    const myRef = useRef<HTMLDivElement>(null);

    const executeScroll = () => {
        if (myRef.current && isMobile) {
            myRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });
        }
    };

    useEffect(() => {
        if (selected) {
            const timer = setTimeout(() => {
                executeScroll();
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [selected]);

    const handleClick = () => {
        if (spaces?.length > 3 && !showModal) {
            setShowModal(true);
        }
    };

    return (
        <Stack>
            <Card
                onClick={handleClick}
                sx={{
                    transformOrigin: 'center',
                    height: '100%',
                    minHeight: '200px',
                    cursor: cursor,
                    transition: 'transform 0.3s ease',
                    ...(cursor === 'pointer' &&
                        spaces.length > 3 && {
                            '&:hover': {
                                transform: 'scale(1.02)',
                            },
                        }),
                }}
                style={{ flex: 1 }}
            >
                <Grid
                    ref={myRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'custom.cardActionBackground',
                        animation: selected
                            ? 'background-change 2s ease-in-out'
                            : 'none',
                        '@keyframes background-change': {
                            '0%': {
                                backgroundColor: theme.palette.faded.main,
                            },

                            '100%': {
                                backgroundColor: 'custom.cardActionBackground',
                            },
                        },
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            paddingTop: 2,
                            height: '60px',
                            gap: 1,
                        }}
                    >
                        <Stack
                            direction='row'
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant='body1'>
                                {SpaceIcons[space.spaceType] || null}
                            </Typography>{' '}
                            <Typography
                                component={'h3'}
                                variant='caption'
                                fontWeight='bold'
                            >
                                {camelCaseToTitleCase(
                                    space?.assignedName ?? space.name,
                                )}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Grid>
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        height: '145px',
                        overflow: 'auto',
                        paddingBottom: '0 !important',
                    }}
                >
                    {getBeddingArray(space).length > 0 && (
                        <Grid container spacing={1} sx={{ pb: 1 }}>
                            <Grid item xs={3} md={6}>
                                <Typography
                                    component={'h4'}
                                    variant='body3'
                                    fontWeight='700'
                                    sx={{ lineHeight: '1.1' }}
                                >
                                    Setup:
                                </Typography>
                            </Grid>
                            <Grid item xs={9} md={6}>
                                <Typography
                                    variant='body3'
                                    fontWeight='500'
                                    sx={{ lineHeight: '1.1' }}
                                >
                                    {getBeddingArray(space).map(
                                        (item: any, i) => (
                                            <Fade in={true} timeout={500}>
                                                <Grid>
                                                    {i > 0} {item[1]}
                                                    &nbsp;
                                                    {camelCaseToLowerCase(
                                                        item[0],
                                                    )}
                                                    {item[1] > 1 &&
                                                        item[0] !== 'couch' &&
                                                        's'}
                                                    {item[1] > 1 &&
                                                        item[0] === 'couch' &&
                                                        'es'}
                                                </Grid>
                                            </Fade>
                                        ),
                                    )}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    {getAmenitiesArray(space).length > 0 && (
                        <Grid container spacing={1} sx={{ pb: 1 }}>
                            <Grid item xs={3} md={6}>
                                <Typography
                                    component={'h4'}
                                    variant='body3'
                                    fontWeight='700'
                                    sx={{ lineHeight: '1.1' }}
                                >
                                    Amenities:
                                </Typography>
                            </Grid>
                            <Grid item xs={9} md={6}>
                                <Typography
                                    variant='body3'
                                    fontWeight='500'
                                    sx={{
                                        lineHeight: '1.1',
                                    }}
                                >
                                    {getAmenitiesArray(space).map(
                                        (amenity: any, i) => (
                                            <Fade in={true} timeout={500}>
                                                <Grid
                                                    sx={{
                                                        pb: 1,
                                                    }}
                                                >
                                                    {i > 0}
                                                    {amenity ===
                                                    'evChargingStation'
                                                        ? 'EV charging station'
                                                        : camelCaseToSentenceCase(
                                                              amenity,
                                                          )}
                                                </Grid>
                                            </Fade>
                                        ),
                                    )}
                                </Typography>{' '}
                            </Grid>
                        </Grid>
                    )}
                    {[SpaceType.Bedroom, SpaceType.PrivateOffice].includes(
                        space.spaceType,
                    ) &&
                        space?.desk > 0 && (
                            <Grid container spacing={1} sx={{ pb: 1 }}>
                                <Grid item xs={3} md={6}>
                                    <Typography
                                        component={'h4'}
                                        variant='body3'
                                        fontWeight='700'
                                        sx={{ lineHeight: '1.1' }}
                                    >
                                        Workspace:
                                    </Typography>
                                </Grid>
                                <Grid item xs={9} md={6}>
                                    <Typography
                                        variant='body3'
                                        fontWeight='500'
                                        sx={{
                                            lineHeight: '1.1',
                                        }}
                                    >
                                        <Fade in={true} timeout={500}>
                                            <Grid>
                                                {space.desk} desk
                                                {space.desk > 1 ? 's' : ''}
                                            </Grid>
                                        </Fade>
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    {space.spaceType === SpaceType.Bedroom &&
                        space?.sleepingCapacity > 0 && (
                            <Grid container spacing={1} sx={{ pb: 1 }}>
                                <Grid item xs={3} md={6}>
                                    <Typography
                                        component={'h4'}
                                        variant='body3'
                                        fontWeight='700'
                                        sx={{ lineHeight: '1.1' }}
                                    >
                                        Max Guests:
                                    </Typography>
                                </Grid>
                                <Grid item xs={9} md={6}>
                                    <Typography
                                        variant='body3'
                                        fontWeight='500'
                                        sx={{
                                            lineHeight: '1.1',
                                        }}
                                    >
                                        <Fade in={true} timeout={500}>
                                            <Grid>
                                                {space.sleepingCapacity} guest
                                                {space.sleepingCapacity > 1
                                                    ? 's'
                                                    : ''}
                                            </Grid>
                                        </Fade>
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    {(space.spaceType === SpaceType.DiningArea ||
                        space.spaceType === SpaceType.Boardroom) &&
                        space?.seatingCapacity > 0 && (
                            <Grid container spacing={1} sx={{ pb: 1 }}>
                                <Grid item xs={3} md={6}>
                                    <Typography
                                        component={'h4'}
                                        variant='body3'
                                        fontWeight='700'
                                        sx={{ lineHeight: '1.1' }}
                                    >
                                        Seating Capacity:
                                    </Typography>
                                </Grid>
                                <Grid item xs={9} md={6}>
                                    <Typography
                                        variant='body3'
                                        fontWeight='500'
                                        sx={{
                                            lineHeight: '1.1',
                                        }}
                                    >
                                        <Fade in={true} timeout={500}>
                                            <Grid>
                                                {space.seatingCapacity} people
                                            </Grid>
                                        </Fade>
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    {space.spaceType === SpaceType.Garage &&
                        space?.parkingCapacity > 0 && (
                            <Grid container spacing={1} sx={{ pb: 1 }}>
                                <Grid item xs={3} md={6}>
                                    <Typography
                                        component={'h4'}
                                        variant='body3'
                                        fontWeight='700'
                                        sx={{ lineHeight: '1.1' }}
                                    >
                                        Parking Capacity:
                                    </Typography>
                                </Grid>
                                <Grid item xs={9} md={6}>
                                    <Typography
                                        variant='body3'
                                        fontWeight='500'
                                        sx={{
                                            lineHeight: '1.1',
                                        }}
                                    >
                                        <Fade in={true} timeout={500}>
                                            <Grid>
                                                {space.parkingCapacity} cars
                                            </Grid>
                                        </Fade>
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                </CardContent>
            </Card>
            {spaces && (
                <IndividualExperienceSpacesModal
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    spaces={spaces}
                    selectedSpace={space}
                    maxNumberOfBeds={maxNumberOfBeds}
                />
            )}
        </Stack>
    );
};

export default SpaceCard;
